import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Activity, 
  Users, 
  Map, 
  User, 
  Settings, 
  Bell, 
  Droplets, 
  Award, 
  Calendar, 
  Clock, 
  Flame, 
  BarChart2, 
  TrendingUp, 
  Heart, 
  ArrowRight, 
  Download,
  Plus
} from 'lucide-react';
import Navigation from '../components/common/Navigation';
import IdleAlert from '../components/Alert/IdleAlert';

// Components
import StepCounter from '../components/dashboard/StepCounter';
import ActivityChart from '../components/dashboard/ActivityChart';
import GoalProgress from '../components/dashboard/GoalProgress';
import Recommendations from '../components/dashboard/Recommendations';
import HydrationReminder from '../components/dashboard/Hydration';


const Dashboard = () => {
  // State for active tab
  const [activeTab, setActiveTab] = useState('overview');
  
const userData = {
  id: "user123",
  name: "Jane Doe",
  email: "jane@example.com",
  
  // Physical data used for calculating hydration needs
  physicalData: {
    useMetric: true,  // true = kg/cm, false = lbs/inches
    weightKg: 70,     // weight in kilograms
    weightLbs: 154,   // weight in pounds
    heightCm: 170,    // height in centimeters
    heightIn: 67,     // height in inches
  },
  
  // Activity level affects hydration requirements
  // Options: "low", "medium", "high"
  activityLevel: "medium",
  
  // User preferences
  preferences: {
    reminderFrequency: "hourly", // how often to remind ("hourly", "every2hours", "every3hours")
    startTime: 8,  // when to start reminders (hour, 24hr format)
    endTime: 20,   // when to stop reminders (hour, 24hr format)
  }
};
const stepData = {
  dailyAverage: 4500,      // Average steps per day - below recommended 5000
  consistency: 0.6,        // User walks 60% of days (below 70% threshold)
  weeklyAverage: 31500,    // Weekly step count
  thisWeek: [
    { day: "Monday", steps: 6200 },
    { day: "Tuesday", steps: 3400 },
    { day: "Wednesday", steps: 0 },
    { day: "Thursday", steps: 7800 },
    { day: "Friday", steps: 5200 },
    { day: "Saturday", steps: 9000 },
    { day: "Sunday", steps: 0 }
  ],
  trend: "improving"       // User's step trend is improving
};
  // Mock data
  const todayStats = {
    steps: 8756,
    distance: 6.4,
    calories: 342,
    activeTime: 85,
    goalSteps: 10000
  };
  
  const weeklySteps = [
    { day: 'Mon', steps: 9123 },
    { day: 'Tue', steps: 7896 },
    { day: 'Wed', steps: 10254 },
    { day: 'Thu', steps: 6543 },
    { day: 'Fri', steps: 8756 },
    { day: 'Sat', steps: 5467 },
    { day: 'Sun', steps: 8234 }
  ];
  
  const activeChallenges = [
    { id: 1, name: "10K Steps Daily", progress: 75, daysLeft: 5 },
    { id: 2, name: "Weekend Warrior", progress: 40, daysLeft: 10 }
  ];
  
  const availableChallenges = [
    { id: 3, name: "Morning Walk Club", participants: 156, duration: "21 days" },
    { id: 4, name: "City Explorer", participants: 89, duration: "14 days" }
  ];
  
  const achievements = [
    { id: 1, name: "First 5K", icon: <Award size={20} /> },
    { id: 2, name: "Week Streak", icon: <Calendar size={20} /> },
    { id: 3, name: "Early Bird", icon: <Clock size={20} /> }
  ];
  
  const recommendedTips = [
    { id: 1, title: "Try Interval Walking", description: "Alternate between brisk and moderate pace for better calorie burn." },
    { id: 2, title: "Hydration Reminder", description: "Remember to drink water before and after your walks." }
  ];
  
  // Mobile navigation items
  const navigationItems = [
    { icon: <Activity size={20} />, label: 'Dashboard', link: '/dashboard', active: true },
    { icon: <Map size={20} />, label: 'Map', link: '/map', active: false },
    { icon: <Users size={20} />, label: 'Challenges', link: '/hot-stepper', active: false },
    { icon: <User size={20} />, label: 'Profile', link: '/profile', active: false },
    { icon: <Settings size={20} />, label: 'Settings', link: '/settings', active: false },
  ];

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <header className="bg-white lg:ml-64 shadow-sm py-4 px-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-primary font-bold text-xl">Stepping4Life</span>
          </div>
          <div className="flex items-center space-x-4">
            <button className="text-text-secondary p-2 rounded-full hover:bg-background-light relative">
              <Bell size={20} />
              <span className="absolute top-1 right-1 bg-accent w-2 h-2 rounded-full"></span>
            </button>
            <Link to="/settings" className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-primary text-white flex items-center justify-center">
                <User size={16} />
              </div>
            </Link>
          </div>
        </div>
      </header>
      <IdleAlert 
        threshold={0.3} 
        onDismiss={() => console.log('Alert dismissed')} 
      />
      {/* Main Content */}
      <main className="max-w-7xl lg:ml-64 mx-auto px-4 py-6">
        {/* Welcome message */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary">Hello, Walker!</h1>
          <p className="text-text-secondary">Let's check your progress today.</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex overflow-x-auto mb-6 bg-white rounded-lg shadow-sm p-1">
          <button 
            onClick={() => setActiveTab('overview')} 
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${activeTab === 'overview' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-background-light'}`}
          >
            Overview
          </button>
          <button 
            onClick={() => setActiveTab('insights')} 
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${activeTab === 'insights' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-background-light'}`}
          >
            Insights & Recommendations
          </button>
          <button 
            onClick={() => setActiveTab('history')} 
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${activeTab === 'history' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-background-light'}`}
          >
            History & Trends
          </button>
          <button 
            onClick={() => setActiveTab('challenges')} 
            className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap ${activeTab === 'challenges' ? 'bg-primary text-white' : 'text-text-secondary hover:bg-background-light'}`}
          >
            Goals & Challenges
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Today's Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Activity size={20} className="text-primary" />
                  <h3 className="text-sm font-medium">Steps</h3>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold">{todayStats.steps.toLocaleString()}</span>
                  <span className="text-text-secondary text-sm mb-1">/ {todayStats.goalSteps.toLocaleString()}</span>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Map size={20} className="text-secondary" />
                  <h3 className="text-sm font-medium">Distance</h3>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold">{todayStats.distance}</span>
                  <span className="text-text-secondary text-sm mb-1">km</span>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Flame size={20} className="text-accent" />
                  <h3 className="text-sm font-medium">Calories</h3>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold">{todayStats.calories}</span>
                  <span className="text-text-secondary text-sm mb-1">kcal</span>
                </div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Clock size={20} className="text-primary" />
                  <h3 className="text-sm font-medium">Active Time</h3>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-2xl font-bold">{todayStats.activeTime}</span>
                  <span className="text-text-secondary text-sm mb-1">min</span>
                </div>
              </div>
            </div>
            
            {/* Current Goal Progress */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Current Goal Progress</h3>
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-full md:w-1/3 flex justify-center">
                  <div className="relative h-48 w-48">
                    <svg viewBox="0 0 100 100" className="h-full w-full">
                      {/* Background circle */}
                      <circle 
                        cx="50" cy="50" r="40" 
                        fill="none" 
                        stroke="#e6e6e6" 
                        strokeWidth="10" 
                      />
                      {/* Progress circle */}
                      <circle 
                        cx="50" cy="50" r="40" 
                        fill="none" 
                        stroke="#4CAF50" 
                        strokeWidth="10" 
                        strokeDasharray="251.2" 
                        strokeDashoffset={251.2 - (251.2 * (todayStats.steps / todayStats.goalSteps))} 
                        strokeLinecap="round"
                        transform="rotate(-90 50 50)" 
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold">{Math.round((todayStats.steps / todayStats.goalSteps) * 100)}%</span>
                      <span className="text-text-secondary text-sm">Daily Goal</span>
                    </div>
                  </div>
                </div>
                
                <div className="w-full md:w-2/3">
                  <StepCounter steps={todayStats.steps} goal={todayStats.goalSteps} />
                  
                  <div className="flex gap-4 mt-6">
                    <Link to="/map" className="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 flex items-center gap-2">
                      <Activity size={16} />
                      Start Walk
                    </Link>
                    <Link to="/hot-stepper" className="bg-secondary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-opacity-90 flex items-center gap-2">
                      <Users size={16} />
                      Join Challenge
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Weekly Steps Chart */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Weekly Activity</h3>
              <ActivityChart data={weeklySteps} />
            </div>
          </div>
        )}

        {activeTab === 'insights' && (
          <div className="space-y-6">
            {/* Personalized Tips */}
            <Recommendations userData={userData} stepData={stepData} />
            
            {/* Fitness Level Assessment */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Fitness Level Assessment</h3>
              <div className="relative w-full h-4 bg-gray-100 rounded-full mb-2">
                <div className="absolute left-0 top-0 h-full w-3/5 bg-primary rounded-full"></div>
                <div className="absolute left-3/5 top-0 transform -translate-x-1/2 -translate-y-full mt-[-8px]">
                  <div className="bg-primary text-white text-xs px-2 py-1 rounded">You are here</div>
                </div>
              </div>
              <div className="flex justify-between text-sm text-text-secondary">
                <span>Beginner</span>
                <span>Intermediate</span>
                <span>Advanced</span>
              </div>
              
              <div className="mt-4">
                <h4 className="font-medium">Your Fitness Level: Intermediate</h4>
                <p className="text-text-secondary text-sm mt-2">
                  You're making great progress! Your walking pace and consistency show you're building good stamina. 
                  Try adding some inclined routes to challenge yourself further.
                </p>
              </div>
            </div>
            
            {/* Pacing and Technique */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Pacing and Technique</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Walking Pace</h4>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold">4.8</span>
                    <span className="text-text-secondary text-sm">km/h</span>
                  </div>
                  <p className="text-text-secondary text-sm mt-1">
                    Your pace is consistent. Try intervals of faster walking to increase calorie burn.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Step Length</h4>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold">0.73</span>
                    <span className="text-text-secondary text-sm">meters</span>
                  </div>
                  <p className="text-text-secondary text-sm mt-1">
                    Your step length is ideal for your height. Maintain good posture for optimal results.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Hydration */}
            <HydrationReminder userData={userData} />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Performance Graph */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Performance Trends</h3>
              <div className="flex space-x-4 mb-4">
                <button className="bg-primary text-white px-3 py-1 rounded-md text-sm">Steps</button>
                <button className="text-text-secondary hover:bg-background-light px-3 py-1 rounded-md text-sm">Distance</button>
                <button className="text-text-secondary hover:bg-background-light px-3 py-1 rounded-md text-sm">Calories</button>
              </div>
              
              {/* Placeholder for line chart */}
              <div className="bg-gray-50 h-64 rounded-lg flex items-center justify-center">
                <div className="h-48 w-full">
                  {/* Simple mock line chart */}
                  <svg viewBox="0 0 100 50" className="h-full w-full">
                    <path 
                      d="M0,35 L10,32 L20,28 L30,30 L40,25 L50,20 L60,15 L70,18 L80,10 L90,12 L100,8" 
                      fill="none" 
                      stroke="#4CAF50" 
                      strokeWidth="2" 
                    />
                    <path 
                      d="M0,35 L10,32 L20,28 L30,30 L40,25 L50,20 L60,15 L70,18 L80,10 L90,12 L100,8" 
                      fill="url(#gradient)" 
                      strokeWidth="0" 
                      opacity="0.2" 
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#4CAF50" />
                        <stop offset="100%" stopColor="#4CAF50" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
              
              <div className="flex justify-between mt-2 text-sm text-text-secondary">
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Sun</span>
              </div>
            </div>
            
            {/* Calendar Heat Map */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Activity Calendar</h3>
              <div className="grid grid-cols-7 gap-2">
                {/* Mock calendar data - day cells for current month */}
                {[...Array(30)].map((_, i) => {
                  // Randomly assign activity level for demonstration
                  const activityLevel = ['low', 'medium', 'high', 'none'][Math.floor(Math.random() * 4)];
                  let bgColor = 'bg-gray-100'; // none
                  
                  if (activityLevel === 'low') bgColor = 'bg-green-100';
                  if (activityLevel === 'medium') bgColor = 'bg-green-300';
                  if (activityLevel === 'high') bgColor = 'bg-green-500';
                  
                  return (
                    <div key={i} className={`h-12 ${bgColor} rounded-md flex items-center justify-center text-sm`}>
                      {i + 1}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center mt-4 gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-100 rounded-sm"></div>
                  <span className="text-xs text-text-secondary">No Activity</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-100 rounded-sm"></div>
                  <span className="text-xs text-text-secondary">Low</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-300 rounded-sm"></div>
                  <span className="text-xs text-text-secondary">Medium</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                  <span className="text-xs text-text-secondary">High</span>
                </div>
              </div>
            </div>
            
            {/* Personal Records */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Personal Records</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp size={16} className="text-primary" />
                    <h4 className="text-sm font-medium">Most Steps</h4>
                  </div>
                  <div className="text-xl font-bold">12,546</div>
                  <div className="text-xs text-text-secondary mt-1">Mar 15, 2025</div>
                </div>
                
                <div className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Map size={16} className="text-primary" />
                    <h4 className="text-sm font-medium">Longest Distance</h4>
                  </div>
                  <div className="text-xl font-bold">8.7 km</div>
                  <div className="text-xs text-text-secondary mt-1">Feb 28, 2025</div>
                </div>
                
                <div className="border border-gray-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Flame size={16} className="text-primary" />
                    <h4 className="text-sm font-medium">Most Calories</h4>
                  </div>
                  <div className="text-xl font-bold">542 kcal</div>
                  <div className="text-xs text-text-secondary mt-1">Mar 22, 2025</div>
                </div>
              </div>
            </div>
            
            {/* Download Reports */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Download Reports</h3>
                <button className="bg-secondary text-white px-4 py-2 rounded-md text-sm flex items-center gap-2">
                  <Download size={16} />
                  Export Data
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'challenges' && (
          <div className="space-y-6">
            {/* Active Challenges */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Active Challenges</h3>
              <div className="space-y-4">
                {activeChallenges.map(challenge => (
                  <div key={challenge.id} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-medium">{challenge.name}</h4>
                      <span className="bg-accent bg-opacity-10 text-accent text-xs px-2 py-1 rounded-full">
                        {challenge.daysLeft} days left
                      </span>
                    </div>
                    
                    <div className="w-full h-2 bg-gray-100 rounded-full mb-2">
                      <div 
                        className="h-full bg-primary rounded-full" 
                        style={{ width: `${challenge.progress}%` }}
                      />
                    </div>
                    
                    <div className="flex justify-between items-center text-sm text-text-secondary">
                      <span>Progress: {challenge.progress}%</span>
                      <Link to={`/challenges/${challenge.id}`} className="text-primary font-medium">
                        View Details
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Available Challenges */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Available Challenges</h3>
                <Link to="/hot-stepper" className="text-primary text-sm font-medium flex items-center gap-1">
                  View All <ArrowRight size={12} />
                </Link>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableChallenges.map(challenge => (
                  <div key={challenge.id} className="border border-gray-100 rounded-lg p-4 hover:border-primary transition-colors">
                    <h4 className="font-medium mb-2">{challenge.name}</h4>
                    <div className="flex justify-between text-sm text-text-secondary mb-4">
                      <div className="flex items-center gap-1">
                        <Users size={14} />
                        <span>{challenge.participants} participants</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>{challenge.duration}</span>
                      </div>
                    </div>
                    <button className="w-full bg-secondary bg-opacity-10 text-secondary px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-1">
                      <Plus size={14} />
                      Join Challenge
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Current Goals */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Current Goals</h3>
              <div className="space-y-4">
                <div className="border border-gray-100 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium">Daily Steps</h4>
                    <button className="text-primary text-sm">Edit</button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative h-16 w-16">
                      <svg viewBox="0 0 100 100" className="h-full w-full">
                        {/* Progress circle */}
                        <circle 
                          cx="50" cy="50" r="40" 
                          fill="none" 
                          stroke="#e6e6e6" 
                          strokeWidth="10" 
                        />
                        <circle 
                          cx="50" cy="50" r="40" 
                          fill="none" 
                          stroke="#4CAF50" 
                          strokeWidth="10" 
                          strokeDasharray="251.2" 
                          strokeDashoffset="62.8" 
                          strokeLinecap="round"
                          transform="rotate(-90 50 50)" 
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                        75%
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">10,000 steps</div>
                      <div className="text-text-secondary text-sm">Resets daily at midnight</div>
                    </div>
                  </div>
                </div>
                
                <div className="border border-gray-100 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium">Weekly Distance</h4>
                    <button className="text-primary text-sm">Edit</button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="relative h-16 w-16">
                      <svg viewBox="0 0 100 100" className="h-full w-full">
                        {/* Progress circle */}
                        <circle 
                          cx="50" cy="50" r="40" 
                          fill="none" 
                          stroke="#e6e6e6" 
                          strokeWidth="10" 
                        />
                        <circle 
                          cx="50" cy="50" r="40" 
                          fill="none" 
                          stroke="#2196F3" 
                          strokeWidth="10" 
                          strokeDasharray="251.2" 
                          strokeDashoffset="125.6" 
                          strokeLinecap="round"
                          transform="rotate(-90 50 50)" 
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                        50%
                      </div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">35 km</div>
                      <div className="text-text-secondary text-sm">Resets every Sunday</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Achievements */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-medium mb-4">Achievements & Badges</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {achievements.map(achievement => (
                  <div key={achievement.id} className="flex flex-col items-center">
                    <div className="h-16 w-16 rounded-full bg-primary bg-opacity-10 flex items-center justify-center text-primary mb-2">
                      {achievement.icon}
                    </div>
                    <span className="text-sm text-center">{achievement.name}</span>
                  </div>
                ))}
                <div className="flex flex-col items-center">
                  <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                    <Award size={20} />
                  </div>
                  <span className="text-sm text-gray-400 text-center">7-Day Streak</span>
                </div>
                <div className="flex flex-col items-center">
                  <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                    <Heart size={20} />
                  </div>
                  <span className="text-sm text-gray-400 text-center">Health Hero</span>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <Link to="/achievements" className="text-primary text-sm font-medium inline-flex items-center gap-1">
                  View All Achievements <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Mobile Navigation */}
      {/* <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-10">
        <div className="flex justify-around items-center">
          {navigationItems.map((item, index) => (
            <Link 
              key={index} 
              to={item.link} 
              className={`flex flex-col items-center py-3 px-2 ${item.active ? 'text-primary' : 'text-text-secondary'}`}
            >
              {item.icon}
              <span className="text-xs mt-1">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav> */}

      {/* Desktop Sidebar - Hidden on mobile */}
      <nav className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white shadow-sm z-20 flex-col">
      <Navigation />
      </nav>
       <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
        <Navigation orientation='horizontal' />
      </div>
      
    </div>
  );
};

export default Dashboard;